import { faPlay } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { Spinner } from "react-bootstrap";

export const getYouTubeDetails = (value) => {
  try {
    const url = new URL(value);
    if (!["https:", "http:"].includes(url.protocol)) return null;

    const hostname = url.hostname.toLowerCase();
    const isShortLink = hostname === "youtu.be";
    const isYouTube =
      hostname === "youtube.com" ||
      hostname.endsWith(".youtube.com") ||
      hostname === "youtube-nocookie.com" ||
      hostname.endsWith(".youtube-nocookie.com");
    if (!isShortLink && !isYouTube) return null;

    const segments = url.pathname.split("/").filter(Boolean);
    const candidate = isShortLink
      ? segments[0]
      : url.searchParams.get("v") ||
        (["embed", "shorts", "live"].includes(segments[0]) ? segments[1] : "");
    const videoId = /^[a-zA-Z0-9_-]{11}$/.test(candidate || "")
      ? candidate
      : "";
    const playlistId = url.searchParams.get("list");
    if (!videoId && !playlistId) return null;

    const embedUrl = new URL(
      `https://www.youtube.com/embed/${videoId || "videoseries"}`,
    );
    if (playlistId) embedUrl.searchParams.set("list", playlistId);
    embedUrl.searchParams.set("rel", "0");
    return { videoId, embedUrl: embedUrl.href };
  } catch {
    return null;
  }
};

const resolutions = ["maxresdefault", "sddefault", "hqdefault"];

const YouTubePreviewPlayer = ({ details, title, playLabel }) => {
  const [playing, setPlaying] = useState(false);
  const [resolutionIndex, setResolutionIndex] = useState(0);
  const [posterReady, setPosterReady] = useState(false);
  const showPlayer =
    playing || !details.videoId || resolutionIndex >= resolutions.length;
  const handleNextResolution = () => {
    setPosterReady(false);
    setResolutionIndex((current) => current + 1);
  };
  const playerUrl = new URL(details.embedUrl);
  if (playing) playerUrl.searchParams.set("autoplay", "1");

  return (
    <div className="ratio ratio-16x9 bg-black rounded-2 overflow-hidden">
      {showPlayer ? (
        <iframe
          src={playerUrl.href}
          title={title}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          className="border-0"
        />
      ) : (
        <button
          type="button"
          className="border-0 p-0 bg-black text-white"
          aria-label={playLabel}
          onClick={() => setPlaying(true)}
        >
          <img
            key={resolutionIndex}
            src={`https://i.ytimg.com/vi/${details.videoId}/${resolutions[resolutionIndex]}.jpg`}
            alt={title}
            className="w-100 h-100"
            style={{ objectFit: "contain", opacity: posterReady ? 1 : 0 }}
            onLoad={(event) => {
              if (event.currentTarget.naturalWidth < 480) {
                handleNextResolution();
              } else {
                setPosterReady(true);
              }
            }}
            onError={handleNextResolution}
          />
          <span className="position-absolute top-50 start-50 translate-middle">
            {posterReady ? (
              <span
                className="d-inline-flex align-items-center justify-content-center bg-danger rounded-2"
                style={{ width: "76px", height: "52px" }}
              >
                <FontAwesomeIcon icon={faPlay} size="lg" />
              </span>
            ) : (
              <Spinner size="sm" />
            )}
          </span>
        </button>
      )}
    </div>
  );
};

const YouTubePreview = ({ url, title, playLabel }) => {
  const details = getYouTubeDetails(url);
  if (!details) return null;

  return (
    <YouTubePreviewPlayer
      key={url}
      details={details}
      title={title}
      playLabel={playLabel || title}
    />
  );
};

export default YouTubePreview;
